// src/features/student-portal/studentPortal.api.ts
// RTK Query endpoints for the Student Portal.
import { baseApi } from '@/services/base.api'
import type {
  StudentCoursesResponse,
  TopicCounts,
  CourseMaterialsResponse,
  RecentlyReadMaterial,
  StudySessionSyncPayload,
  StudentExamScheduleResponse,
  PresignUploadResponse,
  StudentRegisterPayload,
} from './studentPortal.types'

export const studentPortalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /syllabus/my-courses — paginated list of student's enrolled courses
    getMyCourses: builder.query<
      StudentCoursesResponse['data'],
      { page?: number; size?: number; sort?: string } | void
    >({
      query: (params) => ({
        url: '/syllabus/my-courses',
        method: 'GET',
        params: {
          page: params?.page ?? 0,
          size: params?.size ?? 20,
          sort: params?.sort ?? 'name',
        },
      }),
      transformResponse: (response: StudentCoursesResponse) => response.data,
      providesTags: ['Subject'],
    }),

    // GET /syllabus/my-courses/topics-count — count of unlocked / locked topics
    getMyCourseTopicCounts: builder.query<TopicCounts, void>({
      query: () => ({
        url: '/syllabus/my-courses/topics-count',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: TopicCounts }) => response.data,
    }),

    // GET /syllabus/courses/{subjectId}/materials — materials for a specific course/subject
    getCourseMaterials: builder.query<
      CourseMaterialsResponse['data'],
      { subjectId: number | string; page?: number; size?: number; materialType?: 'MONTHLY' | 'ONE_TIME_PURCHASE' }
    >({
      query: ({ subjectId, page = 0, size = 20, materialType = 'MONTHLY' }) => ({
        url: `/syllabus/courses/${subjectId}/materials`,
        method: 'GET',
        params: { page, size, materialType },
      }),
      transformResponse: (response: CourseMaterialsResponse) => response.data,
      providesTags: (_res, _err, arg) => [{ type: 'Subject', id: arg.subjectId }],
    }),

    // GET /tracker/study-sessions/recently-read — last opened study material
    getRecentlyReadMaterial: builder.query<RecentlyReadMaterial | null, void>({
      query: () => ({
        url: '/tracker/study-sessions/recently-read',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data?: RecentlyReadMaterial }) => response.data ?? null,
    }),

    // POST /tracker/study-sessions/sync — sync study time for active materials
    syncStudySessions: builder.mutation<{ success: boolean; message: string }, StudySessionSyncPayload>({
      query: (payload) => ({
        url: '/tracker/study-sessions/sync',
        method: 'POST',
        data: payload,
      }),
    }),

    // GET /exams/student/schedule — active/upcoming exam schedule for student standard
    getStudentExamSchedule: builder.query<
      StudentExamScheduleResponse['data'],
      { standardId?: number | string; page?: number; size?: number }
    >({
      query: ({ standardId = 9, page = 0, size = 20 }) => ({
        url: '/exams/student/schedule',
        method: 'GET',
        params: { standardId: Number(standardId), page, size },
      }),
      transformResponse: (response: StudentExamScheduleResponse) => response.data,
      providesTags: ['Exam'],
    }),

    // GET /syllabus/standards/ids — public available classes / standards
    getStandards: builder.query<number[], void>({
      query: () => ({
        url: '/syllabus/standards/ids',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: number[] }) => response.data ?? [],
    }),

    // GET /institutions/codes?prefix=... — lookup institution by code/name prefix
    lookupInstitutionCodes: builder.query<{ id: number; institutionCode: string; name: string }[], string>({
      query: (prefix) => ({
        url: '/institutions/codes',
        method: 'GET',
        params: { prefix },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response
        if (Array.isArray(response?.data)) return response.data
        if (Array.isArray(response?.data?.data)) return response.data.data
        return []
      },
    }),

    // GET /storage/presign-upload — get Cloudflare R2 pre-signed upload URL
    getPresignUploadUrl: builder.query<
      PresignUploadResponse,
      { fileName: string; contentType: string }
    >({
      query: ({ fileName, contentType }) => ({
        url: '/storage/presign-upload',
        method: 'GET',
        params: { fileName, contentType },
      }),
      transformResponse: (response: { success: boolean; data: PresignUploadResponse }) => response.data,
    }),

    // POST /auth/otp/verify — verify OTP with MSG91 accessToken
    verifyOtp: builder.mutation<
      { verificationToken: string },
      { identity: string; accessToken: string }
    >({
      query: ({ identity, accessToken }) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        data: { identity, accessToken },
      }),
      transformResponse: (response: { success: boolean; data?: { verificationToken?: string; accessToken?: string } }) => ({
        verificationToken: response.data?.verificationToken ?? response.data?.accessToken ?? '',
      }),
    }),

    // POST /auth/register/student — finalize student registration
    registerStudent: builder.mutation<{ success: boolean; message: string }, StudentRegisterPayload>({
      query: (payload) => ({
        url: '/auth/register/student',
        method: 'POST',
        data: payload,
      }),
    }),

      }),
})

export const {
  useGetMyCoursesQuery,
  useGetMyCourseTopicCountsQuery,
  useGetCourseMaterialsQuery,
  useGetRecentlyReadMaterialQuery,
  useSyncStudySessionsMutation,
  useGetStudentExamScheduleQuery,
  useGetStandardsQuery,
  useLazyLookupInstitutionCodesQuery,
  useLazyGetPresignUploadUrlQuery,
  useVerifyOtpMutation,
  useRegisterStudentMutation,
} = studentPortalApi
